import { Alert, AlertIcon, Box, Flex, Input, SimpleGrid, Textarea } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { MaskedInput, MoneyInput } from '../../../components/MaskedInput'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { ApiErrorShape, nowSaoPauloDateTimeLocal, toFormErrors, useAppToast } from '../../../helpers'
import { ICoursesInPerson } from '../../../interfaces/Website/Courses/CoursesInPerson/ICoursesInPerson'
import ContestsService from '../../../services/Contests/ContestsService'
import {
  IContestForSelect,
  MANUAL_PAYMENT_TYPE_LABELS,
  TManualPaymentType
} from '../../../services/Contests/interfaces'
import { Teacher } from '../../../services/Teacher/types'

interface IContestManualApplicationModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
  /** Concurso do filtro da página, que já abre escolhido. */
  initialContestName?: string
}

interface IForm {
  contest_id: number | ''
  course_id: number | ''
  teacher_id: number | ''
  name: string
  cpf: string
  email: string
  phone: string
  amount: number | null
  payment_type: TManualPaymentType | ''
  paid_at: string
  notes: string
}

const EMPTY_FORM: IForm = {
  contest_id: '',
  course_id: '',
  teacher_id: '',
  name: '',
  cpf: '',
  email: '',
  phone: '',
  amount: null,
  payment_type: '',
  paid_at: '',
  notes: ''
}

const EMPTY_ERRORS: Record<keyof IForm, string> = {
  contest_id: '',
  course_id: '',
  teacher_id: '',
  name: '',
  cpf: '',
  email: '',
  phone: '',
  amount: '',
  payment_type: '',
  paid_at: '',
  notes: ''
}

const paymentTypeOptions = Object.entries(MANUAL_PAYMENT_TYPE_LABELS).map(([value, label]) => ({ value, label }))

const todayInSaoPaulo = () => nowSaoPauloDateTimeLocal().slice(0, 10)

/** Inscrição paga na recepção (ABRA-398): entra como paga, com os mesmos e-mails e cupom da compra pelo site. */
export default function ContestManualApplicationModal({
  isOpen,
  onClose,
  onCreated,
  initialContestName
}: IContestManualApplicationModalProps) {
  const contestsService = useMemo(() => new ContestsService(), [])
  const toast = useAppToast()
  const [saving, setSaving] = useState(false)
  const [isLoadingOptions, setIsLoadingOptions] = useState(true)
  const [isLoadingContestOptions, setIsLoadingContestOptions] = useState(false)
  const [contests, setContests] = useState<IContestForSelect[]>([])
  const [courses, setCourses] = useState<ICoursesInPerson[]>([])
  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [form, setForm] = useState<IForm>(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState(EMPTY_ERRORS)

  const loadOptions = async () => {
    setIsLoadingOptions(true)

    try {
      const contestList = await contestsService.getAllForSelect()
      const initialContest = contestList.find((contest) => contest.name === initialContestName)

      setContests(contestList)
      if (initialContest) setForm((prev) => ({ ...prev, contest_id: initialContest.id }))
    } catch (error) {
      toast.error('Erro ao carregar os concursos', error)
    } finally {
      setIsLoadingOptions(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return

    setForm({ ...EMPTY_FORM, paid_at: todayInSaoPaulo() })
    setFormErrors(EMPTY_ERRORS)
    loadOptions()
  }, [isOpen])

  // Cursos, professores e taxa são do concurso escolhido. O `cancelled` descarta a resposta de um
  // concurso que já foi trocado, para a lista de outro não aparecer no lugar.
  useEffect(() => {
    setCourses([])
    setTeachers([])

    const contest = contests.find((item) => item.id === form.contest_id)
    if (!contest) return

    let cancelled = false
    setIsLoadingContestOptions(true)
    contestsService
      .getBySlug(contest.slug)
      .then((detail) => {
        if (cancelled) return
        setCourses(detail.courses)
        setTeachers(detail.teachers)
        setForm((prev) => ({ ...prev, amount: Number(detail.registration_fee) }))
      })
      .catch((error) => {
        if (!cancelled) toast.error('Erro ao carregar os cursos e professores do concurso', error)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingContestOptions(false)
      })

    return () => {
      cancelled = true
      setIsLoadingContestOptions(false)
    }
  }, [form.contest_id, contests])

  const handleSave = async () => {
    // Valor em branco não pode virar R$ 0,00 sem querer: zero é cortesia e precisa ser digitado.
    if (!form.course_id || !form.teacher_id || form.amount === null) {
      setFormErrors({
        ...EMPTY_ERRORS,
        course_id: form.course_id ? '' : 'O curso é obrigatório.',
        teacher_id: form.teacher_id ? '' : 'O professor é obrigatório.',
        amount: form.amount === null ? 'Informe o valor pago.' : ''
      })
      toast.invalidForm()
      return
    }

    setSaving(true)
    setFormErrors(EMPTY_ERRORS)

    try {
      const response = await contestsService.createManualApplication(Number(form.contest_id), {
        name: form.name,
        email: form.email,
        phone: form.phone,
        cpf: form.cpf,
        course_id: Number(form.course_id),
        teacher_id: Number(form.teacher_id),
        amount: form.amount,
        payment_type: form.payment_type as TManualPaymentType,
        paid_at: form.paid_at,
        notes: form.notes.trim() || null
      })
      // A inscrição já está salva mesmo com aviso: só o e-mail/cupom falhou e precisa de reenvio.
      if (response.warning) toast.warning('Inscrição registrada com pendência', response.warning)
      else toast.success('Inscrição registrada')
      onCreated()
      onClose()
    } catch (error) {
      const { status, data } = (error as ApiErrorShape).response ?? {}

      // 400 é a validação de campo; 422 é a regra de negócio (CPF já inscrito, cobrança online
      // pendente), que a API também devolve no `cpf`.
      if ((status === 400 || status === 422) && data?.errors) setFormErrors(toFormErrors(EMPTY_ERRORS, data.errors))
      else toast.error('Erro ao registrar inscrição', error)
    } finally {
      setSaving(false)
    }
  }

  const today = todayInSaoPaulo()

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Inscrição manual"
      onSubmit={handleSave}
      size="wide"
      confirmLabel="Registrar inscrição"
      isLoading={saving}
      isConfirmDisabled={isLoadingOptions || isLoadingContestOptions || !form.contest_id}
    >
      <Flex direction="column" gap={4}>
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Box>
            A inscrição entra como paga. O aluno recebe o e-mail de ingresso e o cupom de convidados, como numa compra
            pelo site.
          </Box>
        </Alert>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormField label="Concurso" error={formErrors.contest_id} isRequired gridColumn="1 / -1">
            <SearchableSelect<number>
              value={form.contest_id}
              onChange={(contestId) => setForm({ ...form, contest_id: contestId, course_id: '', teacher_id: '' })}
              options={contests.map((contest) => ({ value: contest.id, label: contest.name }))}
              isLoading={isLoadingOptions}
              isDisabled={saving}
              entity="concurso"
            />
          </FormField>

          <FormField label="Curso" error={formErrors.course_id} isRequired>
            <SearchableSelect<number>
              value={form.course_id}
              onChange={(courseId) => setForm({ ...form, course_id: courseId })}
              options={courses.map((course) => ({ value: course.id, label: course.name }))}
              isLoading={isLoadingContestOptions}
              isDisabled={saving || !form.contest_id}
              placeholder={form.contest_id ? 'Selecione' : 'Selecione o concurso primeiro'}
              entity="curso"
            />
          </FormField>

          <FormField label="Professor" error={formErrors.teacher_id} isRequired>
            <SearchableSelect<number>
              value={form.teacher_id}
              onChange={(teacherId) => setForm({ ...form, teacher_id: teacherId })}
              options={teachers.map((teacher) => ({ value: teacher.id, label: teacher.name }))}
              isLoading={isLoadingContestOptions}
              isDisabled={saving || !form.contest_id}
              placeholder={form.contest_id ? 'Selecione' : 'Selecione o concurso primeiro'}
              entity="professor"
            />
          </FormField>

          <FormField label="Nome do aluno" error={formErrors.name} isRequired>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} isDisabled={saving} />
          </FormField>

          <FormField label="CPF" error={formErrors.cpf} isRequired>
            <MaskedInput
              mask="cpf"
              value={form.cpf}
              onChange={(cpf) => setForm({ ...form, cpf })}
              isDisabled={saving}
            />
          </FormField>

          <FormField label="E-mail" error={formErrors.email} isRequired>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              isDisabled={saving}
            />
          </FormField>

          <FormField label="Telefone" error={formErrors.phone} isRequired>
            <MaskedInput
              mask="phone"
              value={form.phone}
              onChange={(phone) => setForm({ ...form, phone })}
              isDisabled={saving}
            />
          </FormField>

          <FormField label="Valor pago" error={formErrors.amount} isRequired>
            <MoneyInput value={form.amount} onChange={(amount) => setForm({ ...form, amount })} isDisabled={saving} />
          </FormField>

          <FormField label="Forma de pagamento" error={formErrors.payment_type} isRequired>
            <FormSelect
              value={form.payment_type}
              onChange={(paymentType) => setForm({ ...form, payment_type: paymentType as TManualPaymentType | '' })}
              options={paymentTypeOptions}
              isDisabled={saving}
            />
          </FormField>

          <FormField label="Data do pagamento" error={formErrors.paid_at} isRequired>
            <Input
              type="date"
              value={form.paid_at}
              max={today}
              onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
              isDisabled={saving}
            />
          </FormField>

          <FormField label="Observação" error={formErrors.notes} gridColumn="1 / -1">
            <Textarea
              value={form.notes}
              rows={3}
              maxLength={1000}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              isDisabled={saving}
            />
          </FormField>
        </SimpleGrid>
      </Flex>
    </FormModal>
  )
}
